import { useCallback, type MouseEvent as ReactMouseEvent } from 'react';

import { GridRowParams, MuiEvent } from '@mui/x-data-grid-pro';
import { useSearchParams } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';

import { useColumns } from './hooks/useColumns';
import { EMPLOYEE_ID_PARAMETER_NAME } from '../../constants';
import { Employee } from '@/api/typesense/employees';
import {
  BaseDataTable,
  BaseDataTableProps,
} from '@/components/ui/BaseDataTable';

type EmployeeTableProps = Omit<BaseDataTableProps<Employee>, 'getRowId'> & {
  totalResults: number;
  getRowId?: (row: Employee) => number;
  isPanelOpen?: boolean;
  onPanelOpen?: () => void;
  onPanelOpenAndFocus?: () => void;
};

export const EmployeeTable = ({
  getRowId = (r: Employee) => r.SourceId,
  columns: columnsProp,
  columnWidths,
  isPanelOpen = false,
  onPanelOpen,
  onPanelOpenAndFocus,
  totalResults,
  ...restProps
}: EmployeeTableProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEmployeeId = searchParams.get(EMPLOYEE_ID_PARAMETER_NAME);

  const handleViewDetails = (
    employeeId: string,
    options?: { replace?: boolean },
  ) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set(EMPLOYEE_ID_PARAMETER_NAME, employeeId);
      return newParams;
    }, options);
  };

  const defaultColumns = useColumns({ columnWidths });
  const columns = columnsProp ?? defaultColumns;

  const handleRowClick = (
    params: GridRowParams<Employee>,
    event: MuiEvent<ReactMouseEvent<HTMLElement>>,
  ) => {
    const target = event.target as HTMLElement;
    if (target.closest('a, button')) {
      return;
    }

    const clickedId = String(params.row.SourceId);

    handleViewDetails(clickedId);
    onPanelOpen?.();
  };

  // Arrow key navigation - only when panel open, debounced to avoid spam
  const handleRowNavigate = useDebouncedCallback(
    (row: Employee) => {
      if (!isPanelOpen) return;
      handleViewDetails(String(row.SourceId), { replace: true });
    },
    100,
    { leading: false, trailing: true },
  );

  const handleRowToggle = useCallback(
    (row: Employee, options?: { shouldFocus: boolean }) => {
      const clickedId = String(row.SourceId);

      handleViewDetails(clickedId);

      if (options?.shouldFocus) {
        if (onPanelOpenAndFocus) {
          onPanelOpenAndFocus();
        } else {
          onPanelOpen?.();
        }
      } else {
        onPanelOpen?.();
      }
    },
    [handleViewDetails, onPanelOpen, onPanelOpenAndFocus],
  );

  return (
    <BaseDataTable<Employee>
      getRowId={getRowId}
      onRowClick={handleRowClick}
      onRowNavigate={handleRowNavigate}
      onRowToggle={handleRowToggle}
      columns={columns}
      columnWidths={columnWidths}
      selectedRowId={isPanelOpen ? selectedEmployeeId : null}
      data-automation-id="employees-portfolio-data-grid"
      pinnedColumns={{ left: ['profilePicture', 'Name'] }}
      hasRowNavigation
      totalResults={totalResults}
      {...restProps}
    />
  );
};
